// @ts-nocheck

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils/cn";
import { useToast } from "@/components/ui/ToastProvider";
import { IconLoader, IconImage, IconLink, IconVideo, IconPoll, IconPencilPlus } from "@/components/ui/Icons";
import type { PostType } from "@/types";

const postSchema = z.object({
  title: z.string().min(5, "Title must be at least 5 characters").max(300, "Title must be under 300 characters"),
  content: z.string().max(40000).optional(),
  topic_id: z.string().min(1, "Please select a topic"),
  type: z.enum(["text", "link", "image", "video", "poll"]),
  link_url: z.string().url("Please enter a valid URL").optional().or(z.literal("")),
  tags: z.string().optional(),
  is_nsfw: z.boolean().default(false),
  is_spoiler: z.boolean().default(false),
  is_oc: z.boolean().default(false),
});

type PostForm = z.infer<typeof postSchema>;

interface CreatePostFormProps {
  topics: Array<{ id: string; name: string; slug: string; color: string; is_verified: boolean }>;
  userId: string;
}

const postTypes: { value: PostType; label: string; icon: React.ReactNode }[] = [
  { value: "text", label: "Text", icon: <IconPencilPlus className="w-4 h-4" /> },
  { value: "link", label: "Link", icon: <IconLink className="w-4 h-4" /> },
  { value: "image", label: "Image", icon: <IconImage className="w-4 h-4" /> },
  { value: "video", label: "Video", icon: <IconVideo className="w-4 h-4" /> },
  { value: "poll", label: "Poll", icon: <IconPoll className="w-4 h-4" /> },
];

export function CreatePostForm({ topics, userId }: CreatePostFormProps) {
  const navigate = useNavigate();
  const { success, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [tags, setTags] = useState<string[]>([]);
  const [pollOptions, setPollOptions] = useState(["", ""]);

  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<PostForm>({
    resolver: zodResolver(postSchema),
    defaultValues: { type: "text", is_nsfw: false, is_spoiler: false, is_oc: false },
  });

  const postType = watch("type");
  const content = watch("content", "");
  const title = watch("title", "");

  const addTag = (tag: string) => {
    const clean = tag.toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 30);
    if (clean && !tags.includes(clean) && tags.length < 5) {
      setTags(prev => [...prev, clean]);
    }
    setTagInput("");
  };

  const onSubmit = async (data: PostForm) => {
    setSubmitting(true);
    try {
      const payload = {
        ...data,
        tags,
        poll_options: postType === "poll" ? pollOptions.filter(Boolean) : undefined,
        link_url: postType === "link" ? data.link_url : undefined,
        content: postType === "text" ? data.content : undefined,
      };

      const res = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || "Failed to create post");
      }

      const { post } = await res.json();
      success("Post published!", "Your post is live.");
      navigate(`/post/${post.id}/${post.slug}`);
    } catch (err) {
      showError("Failed to publish", err instanceof Error ? err.message : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Post type tabs */}
      <div className="flex gap-1 bg-surface-overlay rounded-2xl p-1 border border-border">
        {postTypes.map(type => (
          <button
            key={type.value}
            type="button"
            onClick={() => setValue("type", type.value)}
            className={cn(
              "flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium flex-1 justify-center transition-all duration-150",
              postType === type.value
                ? "bg-surface-elevated text-foreground shadow-sm border border-border"
                : "text-text-muted hover:text-foreground"
            )}
          >
            {type.icon}
            <span className="hidden sm:inline">{type.label}</span>
          </button>
        ))}
      </div>

      {/* Topic picker */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Topic <span className="text-destructive">*</span></label>
        <select
          {...register("topic_id")}
          className={cn("input", errors.topic_id && "input-error")}
        >
          <option value="">Select a topic...</option>
          {topics.map(t => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        {errors.topic_id && <p className="text-xs text-destructive">{errors.topic_id.message}</p>}
      </div>

      {/* Title */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-foreground">Title <span className="text-destructive">*</span></label>
          <span className={cn("text-xs tabular-nums", title.length > 280 ? "text-destructive" : "text-text-subtle")}>
            {title.length}/300
          </span>
        </div>
        <input
          {...register("title")}
          type="text"
          placeholder="An interesting and descriptive title..."
          className={cn("input-lg", errors.title && "input-error")}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      {/* Type-specific fields */}
      <AnimatePresence mode="wait">
        {postType === "text" && (
          <motion.div key="text" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-foreground">Content <span className="text-text-subtle font-normal">(optional)</span></label>
              <span className={cn("text-xs", (content?.length || 0) > 38000 ? "text-destructive" : "text-text-subtle")}>
                {content?.length || 0}/40,000
              </span>
            </div>
            <textarea
              {...register("content")}
              rows={6}
              placeholder="Share your thoughts, question, or story..."
              className="input min-h-[150px] resize-y py-3"
            />
          </motion.div>
        )}

        {postType === "link" && (
          <motion.div key="link" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">URL <span className="text-destructive">*</span></label>
            <input
              {...register("link_url")}
              type="url"
              placeholder="https://..."
              className={cn("input-lg", errors.link_url && "input-error")}
            />
            {errors.link_url && <p className="text-xs text-destructive">{errors.link_url.message}</p>}
          </motion.div>
        )}

        {postType === "poll" && (
          <motion.div key="poll" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="space-y-3">
            <label className="text-sm font-medium text-foreground">Poll options</label>
            <div className="space-y-2">
              {pollOptions.map((opt, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={e => setPollOptions(prev => prev.map((p, j) => j === i ? e.target.value : p))}
                    placeholder={`Option ${i + 1}...`}
                    maxLength={100}
                    className="input flex-1"
                  />
                  {pollOptions.length > 2 && (
                    <button type="button"
                      onClick={() => setPollOptions(prev => prev.filter((_, j) => j !== i))}
                      className="w-10 h-10 flex items-center justify-center rounded-xl text-text-subtle hover:text-destructive hover:bg-destructive/10 transition-colors">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              {pollOptions.length < 6 && (
                <button type="button" onClick={() => setPollOptions(prev => [...prev, ""])}
                  className="w-full py-2 border border-dashed border-border rounded-xl text-sm text-text-muted hover:border-brand/50 hover:text-brand transition-colors">
                  + Add option
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Tags */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Tags <span className="text-text-subtle font-normal">(up to 5)</span></label>
        <div className={cn("input min-h-[44px] flex flex-wrap gap-1.5 py-2 cursor-text")}
          onClick={() => document.getElementById("tag-input")?.focus()}>
          {tags.map(tag => (
            <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-muted text-brand text-xs font-medium">
              #{tag}
              <button type="button" onClick={() => setTags(prev => prev.filter(t => t !== tag))}
                className="hover:text-destructive transition-colors">
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </span>
          ))}
          {tags.length < 5 && (
            <input
              id="tag-input"
              value={tagInput}
              onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => {
                if ((e.key === "Enter" || e.key === ",") && tagInput.trim()) {
                  e.preventDefault();
                  addTag(tagInput.trim());
                }
              }}
              onBlur={() => tagInput.trim() && addTag(tagInput.trim())}
              placeholder={tags.length === 0 ? "Add tags (press Enter)..." : ""}
              className="flex-1 min-w-[120px] bg-transparent border-0 outline-none text-sm"
            />
          )}
        </div>
      </div>

      {/* Flags */}
      <div className="flex flex-wrap gap-3">
        {([
          { key: "is_nsfw", label: "NSFW" },
          { key: "is_spoiler", label: "Spoiler" },
          { key: "is_oc", label: "Original Content" },
        ] as const).map(flag => (
          <label key={flag.key} className="flex items-center gap-2 cursor-pointer group">
            <input type="checkbox" {...register(flag.key)} className="sr-only" />
            <div className={cn(
              "w-4 h-4 rounded border-2 flex items-center justify-center transition-all",
              watch(flag.key) ? "bg-brand border-brand" : "border-input group-hover:border-brand/50"
            )}>
              {watch(flag.key) && (
                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
            <span className="text-sm text-text-muted group-hover:text-foreground transition-colors">{flag.label}</span>
          </label>
        ))}
      </div>

      {/* Submit */}
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={() => navigate(-1)}
          className="btn-secondary btn-lg">
          Cancel
        </button>
        <button type="submit" disabled={submitting} className="btn-primary btn-lg flex-1">
          {submitting ? <><IconLoader className="w-4 h-4" /> Publishing...</> : "Publish post"}
        </button>
      </div>
    </form>
  );
}
